import { ICouponApplied } from 'types/coupon';
import { computeCouponDiscount } from 'services/coupon/compute-coupon-discount';
import { fixDateStr } from 'utils/time';
import { List } from 'immutable';
import { changeMultiShopStatus } from 'services/coupon/change-coupon-status';
import { CouponGoodsItem } from 'types/cart';
import {
    computePlatformCouponBaseAmount,
    computeShopCouponBaseAmount
} from 'services/coupon/coupon-for-goods-list';
import { classifyCouponByShop } from 'services/coupon/classify-coupon-by-shop';
import { getPlatformCoupon, getShopCoupon } from 'services/coupon/get-coupon-list';

export const getMaximumCoupon = (couponList: ICouponApplied[], baseAmount: number) => {
    if (!Array.isArray(couponList) || couponList.length === 0) {
        return;
    }

    let choseCouponIndex = -1;
    let maxDiscount = 0;

    couponList.forEach((coupon, index) => {
        const couponDiscount = computeCouponDiscount(coupon, baseAmount);
        if (couponDiscount > maxDiscount) {
            maxDiscount = couponDiscount;
            choseCouponIndex = index;
        }
    });

    if (choseCouponIndex > -1) {
        return couponList[choseCouponIndex];
    }

    return;
};

export const sortCoupon = (couponList: ICouponApplied[]) => {
    return List(couponList)
        .sort((item1, item2) => {
            const time1 = new Date(fixDateStr(item1.createTime)).getTime();
            const time2 = new Date(fixDateStr(item2.createTime)).getTime();

            // time1小于time2，time1的时间早于time2的时间，time2排在time1的前面
            return time1 - time2 < 0 ? 1 : -1;
        })
        .toJS();
};

interface IMergedCoupon {
    coupon: Record<string, ICouponApplied | undefined>;
    goods: Record<string, CouponGoodsItem[]>;
    discount: number;
}

interface INeedMergeCoupon {
    coupon?: ICouponApplied[];
    list: CouponGoodsItem[];
    shopId: string;
}
// 组合店铺券和平台券
function mergeCouponList(
    mergedList: IMergedCoupon[],
    newList: INeedMergeCoupon,
    platformCoupon?: ICouponApplied[]
) {
    const newMergedList: IMergedCoupon[] = [];
    if (!newList.shopId) {
        const shopCouponLen = mergedList.length;
        const platCouponLen = (platformCoupon && platformCoupon.length) || 1;
        for (let i = 0; i < shopCouponLen; i++) {
            let totalGoodsList: CouponGoodsItem[] = Object.values(mergedList[i].goods).reduce(
                (item1, item2) => item1.concat(item2),
                []
            );
            for (let j = 0; j < platCouponLen; j++) {
                const coupon = (platformCoupon && platformCoupon[j]) || undefined;
                const base = computePlatformCouponBaseAmount(totalGoodsList, coupon);
                const oldMerged = { ...mergedList[i] };
                if (base.discount > 0) {
                    oldMerged.coupon = { ...oldMerged.coupon, platform: coupon };
                }
                oldMerged.goods = { ...oldMerged.goods, platform: base.list };
                oldMerged.discount = oldMerged.discount + base.discount;
                newMergedList.push({ ...oldMerged });
            }
        }
    } else {
        const shopCouponLen = mergedList.length;
        const needMergedLen = (newList.coupon && newList.coupon.length) || 1;
        for (let i = 0; i < shopCouponLen; i++) {
            for (let j = 0; j < needMergedLen; j++) {
                const coupon = (newList.coupon && newList.coupon[j]) || undefined;
                const base = computeShopCouponBaseAmount(newList.list, coupon);
                const oldMerged = { ...mergedList[i] };
                if (base.discount > 0) {
                    oldMerged.coupon = { ...oldMerged.coupon, [newList.shopId]: coupon };
                }
                oldMerged.goods = { ...oldMerged.goods, [newList.shopId]: base.list };
                oldMerged.discount = oldMerged.discount + base.discount;
                newMergedList.push({ ...oldMerged });
            }
        }
    }
    return newMergedList;
}

function initMergedShop(list: CouponGoodsItem[], shopId: string, coupon?: ICouponApplied[]) {
    const res: IMergedCoupon[] = [];

    const couponLen = (coupon && coupon.length) || 1;
    for (let i = 0; i < couponLen; i++) {
        const couponNow = (coupon && coupon[i]) || undefined;
        const base = computeShopCouponBaseAmount(list, couponNow);
        if (base.discount > 0) {
            res.push({
                coupon: { [shopId]: couponNow },
                goods: { [shopId]: base.list },
                discount: base.discount
            });
        } else {
            res.push({
                coupon: {},
                goods: { [shopId]: base.list },
                discount: base.discount
            });
        }
    }

    return res;
}

export const computeMaximumCouponForGoodsList = (
    couponListInit: ICouponApplied[],
    goodsList: Record<string, CouponGoodsItem[]>,
    needSelected?: boolean
) => {
    // 优惠券初始化
    const couponList = couponListInit.map((item) => {
        return { ...item, selected: false, explain: [], disable: false };
    });
    const shops = Object.keys(goodsList);
    const shopLen = shops.length;

    const coupon = classifyCouponByShop(couponList);
    const platformCoupons = coupon.platform;

    // 遍历优惠券
    let mergedList = initMergedShop(goodsList[shops[0]], shops[0], coupon[shops[0]]);
    for (let i = 1; i < shopLen + 1; i++) {
        mergedList = mergeCouponList(
            mergedList,
            { coupon: coupon[shops[i]], list: goodsList[shops[i]], shopId: shops[i] },
            platformCoupons
        );
    }
    // 选择最优券
    let max: {
        discount: number;
        coupon?: Record<string, ICouponApplied | undefined>;
        goods: CouponGoodsItem[];
        available: ICouponApplied[];
    } = {
        discount: 0,
        coupon: undefined,
        goods: [],
        available: [...couponList]
    };

    mergedList.forEach((item) => {
        if (item.discount > max.discount) {
            max.discount = item.discount;
            max.coupon = item.coupon;
            max.goods = item.goods.platform;
        }
    });
    if (max.coupon) {
        // 优惠券列表排序，选择状态更新
        const ids = Object.values(max.coupon).map((item) => item && item.id);
        max.available = sortCoupon(max.available.filter((item) => ids.indexOf(item.id) === -1));
        Object.values(max.coupon).forEach((coupon) => {
            if (coupon) {
                max.available.unshift(coupon);
            }
        });

        if (needSelected === true || needSelected === undefined) {
            changeMultiShopStatus(max.available, max.coupon, max.goods);
        }
    } else {
        max.goods = Object.values(goodsList).reduce((item1, item2) => item1.concat(item2), []);
        max.available = sortCoupon(max.available);
        const newClassify = classifyCouponByShop(max.available);
        Object.keys(newClassify).forEach((shopId) => {
            if (shopId !== 'platform') {
                getShopCoupon(
                    newClassify[shopId],
                    max.goods.filter((item) => item.shopId === shopId)
                );
            }
        });
        if (newClassify.platform) {
            getPlatformCoupon(newClassify.platform, max.goods);
        }
    }

    return max;
};
